/**
 * FinancialSelect.js — Custom themed dropdown, portal-based.
 *
 * Renders the dropdown list via ReactDOM.createPortal into document.body so it
 * escapes any parent stacking context created by backdrop-filter, overflow:hidden,
 * or transform. Positioned via getBoundingClientRect() on the trigger element.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

export default function FinancialSelect({
  value,
  onChange,
  options = [],
  placeholder = "",
  T,
  style = {},
  disabled = false,
}) {
  const [open, setOpen]       = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef            = useRef(null);
  const listRef               = useRef(null);

  // Normalise option shape to {value, label}
  const normalised = options.map(o =>
    typeof o === "object" ? o : { value: o, label: o }
  );

  const selected = normalised.find(o => String(o.value) === String(value));

  // Position the portal list below the trigger
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropPos({
      top:   rect.bottom + window.scrollY + 3,
      left:  rect.left   + window.scrollX,
      width: rect.width,
    });
  }, [open]);

  // Close on outside click or scroll
  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      if (
        triggerRef.current && !triggerRef.current.contains(e.target) &&
        listRef.current   && !listRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    const closeScroll = (e) => {
      // Ignore scrolls that happen inside the dropdown list itself
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", close);
    window.addEventListener("scroll", closeScroll, true);
    return () => {
      document.removeEventListener("mousedown", close);
      window.removeEventListener("scroll", closeScroll, true);
    };
  }, [open]);

  const handleSelect = (val) => {
    onChange(val);
    setOpen(false);
  };

  const triggerStyle = {
    display:        "flex",
    alignItems:     "center",
    justifyContent: "space-between",
    gap:            6,
    background:     T.inputBg,
    border:         "1px solid " + T.inputBdr,
    borderRadius:   7,
    color:          value ? T.inputText : T.textMuted,
    fontSize:       12,
    padding:        "5px 8px 5px 9px",
    cursor:         disabled ? "not-allowed" : "pointer",
    userSelect:     "none",
    opacity:        disabled ? 0.5 : 1,
    fontFamily:     T.font,
    width:          "100%",
    boxSizing:      "border-box",
  };

  const optBase = {
    padding:    "7px 13px",
    fontSize:   12,
    cursor:     "pointer",
    fontFamily: T.font,
    whiteSpace: "nowrap",
    lineHeight: 1.4,
  };

  const chevron = (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none"
      style={{ flexShrink: 0, opacity: 0.55, transition: "transform 0.15s",
               transform: open ? "rotate(180deg)" : "none" }}>
      <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  // Portal dropdown list
  const dropdownList = open && ReactDOM.createPortal(
    <div
      ref={listRef}
      style={{
        position:    "absolute",
        top:         dropPos.top,
        left:        dropPos.left,
        minWidth:    dropPos.width,
        background:  T.isDark ? "#0b1a30" : "#ffffff",
        border:      "1px solid " + (T.isDark ? "rgba(255,255,255,0.14)" : "rgba(3,78,123,0.20)"),
        borderRadius:8,
        zIndex:      99999,
        maxHeight:   240,
        overflowY:   "auto",
        boxShadow:   T.isDark
          ? "0 8px 32px rgba(0,0,0,0.70)"
          : "0 4px 20px rgba(0,40,100,0.16)",
        padding:     "4px 0",
      }}
    >
      {/* Placeholder / clear option */}
      {placeholder && (
        <div
          style={{
            ...optBase,
            color:      T.textMuted,
            background: !value
              ? (T.isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)")
              : "transparent",
          }}
          onMouseEnter={e => {
            if (value) e.currentTarget.style.background = T.isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.07)";
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = !value
              ? (T.isDark ? "rgba(255,255,255,0.06)" : "rgba(3,78,123,0.06)")
              : "transparent";
          }}
          onClick={() => handleSelect("")}
        >
          {placeholder}
        </div>
      )}

      {normalised.map(o => {
        const isActive = String(o.value) === String(value);
        return (
          <div
            key={o.value}
            style={{
              ...optBase,
              color:      isActive ? T.accentText : (T.isDark ? "rgba(255,255,255,0.88)" : "#021d35"),
              fontWeight: isActive ? 700 : 400,
              background: isActive
                ? (T.isDark ? "rgba(247,206,11,0.13)" : "rgba(247,206,11,0.12)")
                : "transparent",
            }}
            onMouseEnter={e => {
              if (!isActive) e.currentTarget.style.background = T.isDark ? "rgba(255,255,255,0.07)" : "rgba(3,78,123,0.07)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = isActive
                ? (T.isDark ? "rgba(247,206,11,0.13)" : "rgba(247,206,11,0.12)")
                : "transparent";
            }}
            onClick={() => handleSelect(o.value)}
          >
            {o.label}
          </div>
        );
      })}
    </div>,
    document.body
  );

  return (
    <div style={{ position: "relative", display: "inline-block", minWidth: 110, ...style }}>
      <div
        ref={triggerRef}
        style={triggerStyle}
        onClick={() => !disabled && setOpen(o => !o)}
        title={selected?.label || placeholder}
      >
        <span style={{
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1,
          color: value ? T.inputText : T.textMuted,
        }}>
          {selected ? selected.label : <span style={{ color: T.textMuted }}>{placeholder}</span>}
        </span>
        {chevron}
      </div>
      {dropdownList}
    </div>
  );
}
